/**
 * Export utilities for downloading visualization data
 */

/**
 * Convert data array to CSV format
 */
export function dataToCSV(data: Record<string, unknown>[], columns?: string[]): string {
  if (data.length === 0) return '';

  // Get all unique keys if columns not specified
  const keys = columns || [...new Set(data.flatMap((item) => Object.keys(item)))];

  // Create header row
  const header = keys.join(',');

  // Create data rows
  const rows = data.map((item) =>
    keys
      .map((key) => {
        const value = item[key];
        // Handle special cases
        if (value === null || value === undefined) return '';
        if (typeof value === 'object') return JSON.stringify(value);
        // Escape commas and quotes
        const str = String(value);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      })
      .join(',')
  );

  return [header, ...rows].join('\n');
}

/**
 * Download a file with the given content
 */
export function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Download data as CSV file
 */
export function downloadCSV(data: Record<string, unknown>[], filename = 'data.csv'): void {
  const csv = dataToCSV(data);
  downloadFile(csv, filename, 'text/csv;charset=utf-8');
}

/**
 * Download data as JSON file
 */
export function downloadJSON(data: unknown, filename = 'data.json'): void {
  const json = JSON.stringify(data, null, 2);
  downloadFile(json, filename, 'application/json');
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      return true;
    } catch {
      return false;
    } finally {
      document.body.removeChild(textArea);
    }
  }
}

/**
 * Generate a timestamped filename
 */
export function generateFilename(prefix: string, extension: string): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  return `${prefix}-${timestamp}.${extension}`;
}
